<template>
  <div
    class="mobile-filter-actions"
    :class="{
      'is-sticky': isSticky,
    }"
  >
    <!-- Search Bar -->
    <div class="search-cart-row">
      <div class="search-bar-mobile">
        <span class="search-icon">
          <img src="/images/maker-cart/union.svg" alt="Search icon" />
        </span>
        <input
          class="search-input"
          type="text"
          :value="searchQuery"
          @input="handleSearchInput"
          placeholder="Search lesson library modules"
        />
        <button
          v-if="searchQuery"
          type="button"
          class="remove-icon"
          aria-label="Clear search"
          @click="clearSearch"
        >
          <img src="/images/close.svg" alt="Clear search" />
        </button>
      </div>
    </div>

    <!-- Action Buttons Row -->
    <div class="actions-row">
      <div class="action-row-filters">
        <button
          type="button"
          class="action-button filter-button"
          aria-label="Open filters"
          :aria-pressed="isFilterActive"
          @click="filterClicked"
        >
          <FilterIcon color="currentColor" />
        </button>
        <div class="sort-selector">
          <SingleSelect
            :options="sortOptions"
            prefix="Sort by: "
            :model-value="selectedSort"
            @update:model-value="$emit('update:selectedSort', $event)"
            @change="onSortSelectChange"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { defineProps, defineEmits, computed } from "vue";
import SingleSelect from "@/components/common/SingleSelect.vue";
import { FilterIcon } from "@/components/icons";

defineProps({
  searchQuery: {
    type: String,
    default: "",
  },
  selectedSort: {
    type: String,
    default: "id",
  },
  isSticky: {
    type: Boolean,
    default: false,
  },
  isFilterActive: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  "update:searchQuery",
  "update:selectedSort",
  "search",
  "clear-search",
  "filter-clicked",
  "sort-changed",
]);

const sortOptions = computed(() => {
  return [
    { value: "id", text: "<b><strong>Published Date</strong></b>" },
    { value: "like_count", text: "<b><strong>Popularity</strong></b>" },
  ];
});

const handleSearchInput = (event) => {
  emit("update:searchQuery", event.target.value);
  emit("search", event.target.value);
};

const clearSearch = () => {
  emit("update:searchQuery", "");
  emit("clear-search");
};

const filterClicked = () => {
  emit("filter-clicked");
};

const onSortSelectChange = (payload) => {
  if (!payload || !payload.value) return;
  emit("update:selectedSort", payload.value);
  emit("sort-changed", payload.value);
};
</script>

<style scoped>
.mobile-filter-actions {
  background: white;
  padding: 8px 16px;
  transition: all 0.3s ease;
  position: relative;
  z-index: 10;
  width: 100%;
}

.mobile-filter-actions.is-sticky {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.search-cart-row {
  display: flex;
  gap: 8px;
  align-items: center;
  margin-bottom: 8px;
}

.search-bar-mobile {
  flex: 1;
  position: relative;
  display: flex;
  align-items: center;
  background: white;
  border: 1px solid #6c737f;
  border-radius: 8px;
  height: 56px;
  overflow: hidden;
}

.search-icon {
  position: absolute;
  left: 16px;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
}

.search-icon img {
  width: 24px;
  height: 24px;
}

.search-input {
  flex: 1;
  padding: 4px 16px 4px 48px;
  border: none;
  outline: none;
  background: transparent;
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-big, 20px);
  font-weight: 500;
  color: #3b4648;
  height: 100%;
}

.search-input::placeholder {
  color: #6c737f;
  font-family: inherit;
  font-size: inherit;
  font-weight: 500;
}

.remove-icon {
  position: absolute;
  right: 16px;
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  z-index: 2;
}

.remove-icon img {
  width: 16px;
  height: 16px;
}

.actions-row {
  width: 100%;
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: space-between;
}

.action-row-filters {
  display: flex;
  gap: 8px;
  align-items: center;
  width: 100%;
}

.action-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 8px 12.8px;
  background: white;
  border: 1px solid var(--primary-blue, #003d6a);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: "League Spartan", sans-serif;
  font-size: 12px;
  font-weight: 700;
  color: var(--primary-blue, #003d6a);
  height: 32px;
  white-space: nowrap;
}

.action-button:hover {
  background: #f2f3fb;
}

.action-button svg {
  width: 16px;
  height: 16px;
}

.filter-button {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  padding: 8px;
  border: 0.8px solid var(--primary-blue, #003d6a);
}

.filter-button[aria-pressed="true"],
.filter-button:active {
  border-color: var(--white, #ffffff) !important;
  background: var(--blue-dark-2, #061f32) !important;
  color: var(--white, #ffffff) !important;
}

.filter-button[aria-pressed="true"]:hover {
  background: var(--blue-dark-2, #061f32) !important;
}

/* Override SingleSelect styles for mobile */
.sort-selector {
  flex: 1 1 auto;
  min-width: 0;
}

.sort-selector :deep(.single-select) {
  width: fit-content;
  max-width: 100%;
}

.sort-selector :deep(.select-trigger) {
  box-sizing: border-box;
  height: 32px;
  min-height: 32px;
  padding: 10px 16px;
  gap: 8px;
  border-color: var(--primary-blue, #003d6a);
  border-radius: 8px;
  background-color: var(--white, #ffffff);
  color: var(--primary-blue, #003d6a);
}

.sort-selector :deep(.select-trigger:hover) {
  border-color: var(--primary-blue, #003d6a);
  background-color: var(--white, #ffffff);
}

.sort-selector :deep(.select-trigger.active) {
  border-color: var(--blue-dark-2);
  background-color: var(--blue-dark-2);
}

.sort-selector :deep(.select-text) {
  font-family: var(--font-family-primary);
  font-size: var(--font-size-title-big);
  font-weight: var(--font-weight-regular);
  line-height: var(--line-height-title-big);
  letter-spacing: 0.03px;
  color: var(--primary-blue, #003d6a);
}

.sort-selector :deep(.select-trigger.has-value .select-text) {
  color: var(--primary-blue, #003d6a);
}

.sort-selector :deep(.prefix-text) {
  font-size: var(--fs-small);
  font-weight: var(--font-weight-regular);
  line-height: var(--line-height-title-big);
  letter-spacing: 0.03px;
  color: var(--primary-blue, #003d6a);
}

.sort-selector :deep(.prefix-text_value) {
  font-size: var(--fs-small);
  font-weight: var(--font-weight-bold);
  line-height: var(--line-height-title-big);
  letter-spacing: 0.03px;
  color: var(--primary-blue, #003d6a);
}

.sort-selector :deep(.select-trigger.active .select-text),
.sort-selector :deep(.select-trigger.active .prefix-text),
.sort-selector :deep(.select-trigger.active .prefix-text_value),
.sort-selector :deep(.select-trigger.active .dropdown-arrow) {
  color: var(--white, #ffffff);
}

.sort-selector :deep(.select-dropdown .option-text) {
  font-size: var(--fs-small);
}

.sort-selector :deep(.dropdown-arrow) {
  width: 16px;
  height: 16px;
  color: var(--primary-blue, #003d6a);
}

.sort-selector :deep(.select-dropdown) {
  left: 0 !important;
  right: auto !important;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
}

/* Ensure proper z-index for sticky behavior */
.mobile-filter-actions.is-sticky {
  z-index: 1000;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  width: 100%;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}
</style>
